import React, { useEffect, useMemo, useState } from 'react';
import { AdminNotificationDto, adminApi, isUnauthorized } from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminNotificationsScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

const AdminNotificationsScreen: React.FC<AdminNotificationsScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const colors = useMemo(
    () => ({
      card: theme.cardBg,
      border: theme.border,
      text: theme.text,
      subText: theme.subText,
      hover: theme.hoverBg,
      radius: theme.radius,
    }),
    [theme]
  );

  const [items, setItems] = useState<AdminNotificationDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const list = await adminApi.listNotifications(token, { take: 1000, skip: 0 });
        if (cancelled) return;
        setItems(list);
      } catch (e) {
        if (isUnauthorized(e)) {
          onUnauthorized();
          return;
        }
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed loading notifications');
        setItems(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [token, onUnauthorized]);

  const rows = useMemo(() => {
    return (items ?? []).map((n) => {
      const created = new Date(n.createdAtUtc);
      const read = n.readAtUtc ? new Date(n.readAtUtc) : null;
      return {
        ...n,
        createdText: Number.isFinite(created.getTime()) ? created.toLocaleString() : n.createdAtUtc,
        readText: read && Number.isFinite(read.getTime()) ? read.toLocaleString() : (n.readAtUtc ?? ''),
      };
    });
  }, [items]);

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 900,
    color: colors.subText,
    padding: '10px 10px',
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 700,
    color: colors.text,
    padding: '10px 10px',
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  };

  return (
    <div
      style={{
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: colors.radius,
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 900, color: colors.text }}>Notifications</div>
        <div style={{ fontSize: 12, fontWeight: 800, color: colors.subText }}>
          {loading ? 'Loading…' : `${rows.length} items`}
        </div>
      </div>

      {error ? (
        <div
          style={{
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.hover,
            borderRadius: colors.radius,
            padding: 12,
            color: colors.text,
            fontSize: 13,
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
          <thead>
            <tr>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>User</th>
              <th style={thStyle}>Source</th>
              <th style={thStyle}>Symbol</th>
              <th style={thStyle}>Title</th>
              <th style={thStyle}>Body</th>
              <th style={thStyle}>Read</th>
              <th style={thStyle}>Read At</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr>
                <td style={{ ...tdStyle, color: colors.subText }} colSpan={8}>
                  No notifications found.
                </td>
              </tr>
            ) : null}

            {rows.map((n) => {
              return (
                <tr key={String(n.id)}>
                  <td style={tdStyle}>{n.createdText}</td>
                  <td style={tdStyle}>{(n.username ?? '').toString() || `#${n.userId}`}</td>
                  <td style={tdStyle}>{n.source}</td>
                  <td style={tdStyle}>{n.symbol}</td>
                  <td style={tdStyle}>{n.title}</td>
                  <td style={{ ...tdStyle, color: colors.subText, fontWeight: 800 }}>{n.body}</td>
                  <td style={tdStyle}>{n.isRead ? 'Yes' : 'No'}</td>
                  <td style={tdStyle}>{n.isRead ? n.readText : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminNotificationsScreen;
